import { useMutation } from "@apollo/client";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { useFeatureFlags } from "@app.automotus.io/components/hooks";
import {
  REVIEW_ENFORCEMENT_INTENT,
  ReviewEnforcementIntentData,
  ReviewEnforcementIntentVars,
  EnforcementIntentDetail,
} from "common/graphql";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import { FEATURE_FLAGS } from "@curbsuite/constants";

export const NoticeReviewActions: React.FC<NoticeReviewActionsProps> = ({
  isIssueEnabled,
  isVoidEnabled,
  requiresPolicySelection,
  selectedEnforcementPolicyId,
  selectedOperatorId,
  intent,
}) => {
  const { id } = intent;
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);
  const [deniedReason, setDeniedReason] = React.useState<{ label: string; value: string } | null>(null);
  const { isEnabled } = useFeatureFlags();
  const { publishSnackbarMessage } = useSnackPackContext();
  const [isVoiding, setIsVoiding] = React.useState(false);
  const [isIssuing, setIsIssuing] = React.useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [reviewEnforcementIntent, { loading: isSubmitting }] = useMutation<
    ReviewEnforcementIntentData,
    ReviewEnforcementIntentVars
  >(REVIEW_ENFORCEMENT_INTENT);

  const isPolicySelectionPending = Boolean(requiresPolicySelection && !selectedEnforcementPolicyId);
  const isNoticeNumberMissing = Boolean(
    intent.policy.workflowIntegrationConfig?.officerInputNoticeNumberRegex && !intent.officerInputNoticeNumber,
  );
  const isDueDateMissing = Boolean(intent.policy.workflowIntegrationConfig?.isDueDateRequired && !intent.dueAt);

  const issueDisabledReason = isPolicySelectionPending
    ? "Select a violation code before issuing"
    : isNoticeNumberMissing
    ? "Enter the notice number before issuing"
    : isDueDateMissing
    ? "Enter the court date before issuing"
    : null;

  const handleCloseVoidDialog = () => {
    setIsVoidDialogOpen(false);
    setDeniedReason(null);
  };

  const handleVoidEnforcementIntent = () => {
    setIsVoiding(true);
    reviewEnforcementIntent({
      variables: { intentId: id, approve: false, deniedReason: deniedReason?.value || "" },
      onError(err) {
        console.error(err);
        setIsVoiding(false);
        publishSnackbarMessage({
          message: "Failed to void notice",
          severity: "error",
        });
      },
      onCompleted() {
        setIsVoiding(false);
        publishSnackbarMessage({
          message: "Void successful",
          severity: "success",
        });
        navigate(-1);
      },
    });
  };

  const handleIssueEnforcementIntent = () => {
    setIsIssuing(true);
    reviewEnforcementIntent({
      variables: {
        intentId: id,
        approve: true,
        ...(selectedEnforcementPolicyId ? { selectedEnforcementPolicyId } : {}),
        ...(selectedOperatorId ? { operatorAccountId: selectedOperatorId } : {}),
      },
      onError(err) {
        console.error(err);
        setIsIssuing(false);
        publishSnackbarMessage({
          message: "Failed to issue notice",
          severity: "error",
        });
      },
      onCompleted() {
        setIsIssuing(false);
        publishSnackbarMessage({
          message: "Issue successful",
          severity: "success",
        });
        navigate(-1);
      },
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "space-between", sm: "flex-end" },
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Button
            disabled={isSubmitting || !isVoidEnabled}
            variant="contained"
            color="error"
            onClick={() => setIsVoidDialogOpen(true)}
            fullWidth={isMobile}
          >
            Void
          </Button>
          <LoadingButton
            disabled={isSubmitting || !isIssueEnabled || isPolicySelectionPending}
            loading={isIssuing}
            variant="contained"
            color="primary"
            onClick={
              isEnabled(FEATURE_FLAGS.NOTICE_REVIEW_REQUIRE_CONFIRM_ADVISE_DRIVER)
                ? () => setIsConfirmDialogOpen(true)
                : handleIssueEnforcementIntent
            }
            fullWidth={isMobile}
          >
            Issue
          </LoadingButton>
        </Box>
        {isVoidEnabled && issueDisabledReason && (
          <Alert severity="warning" sx={{ mt: 1, alignSelf: "flex-end" }}>
            {issueDisabledReason}
          </Alert>
        )}
      </Box>
      <Dialog open={isVoidDialogOpen} onClose={handleCloseVoidDialog} sx={{ px: 4 }}>
        <DialogTitle>
          <Typography variant="h4">Voiding a Notice?</Typography>
          <Typography variant="body1">Select from the below options</Typography>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="subtitle1">Tell us what is wrong with the notice</Typography>
            <Typography variant="caption">
              Fill out this field if you'd like! It really helps our team at Automotus make any necessary updates to our
              software if there's an issue we can address.
              <br /> <br />
              Thanks for your help!
            </Typography>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Autocomplete
              options={VOID_REASONS}
              fullWidth
              disabled={isSubmitting}
              value={deniedReason}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setDeniedReason(value)}
              renderInput={(params) => <TextField {...params} required label="Reason for Void" />}
            />
            <Stack sx={{ mt: 6 }} spacing={3}>
              <LoadingButton
                loading={isVoiding}
                disabled={!deniedReason || isSubmitting}
                fullWidth
                variant="contained"
                color="error"
                onClick={handleVoidEnforcementIntent}
              >
                Void Notice
              </LoadingButton>
              <Button disabled={isSubmitting} fullWidth variant="contained" onClick={handleCloseVoidDialog}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue Citation</DialogTitle>
        <DialogContent>
          <Typography variant="h7">Was the driver advised of the citation or was the vehicle empty?</Typography>
        </DialogContent>
        <DialogActions>
          <Button fullWidth onClick={() => setIsConfirmDialogOpen(false)}>
            No
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => {
              setIsConfirmDialogOpen(false);
              handleIssueEnforcementIntent();
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const VOID_REASONS = [
  "Vehicle Left",
  "Actively Parking",
  "Armored Car",
  "Bad Weather",
  "Client Request",
  "Construction",
  "Commercial Vehicle",
  "Consul Plate",
  "Dark Environment",
  "Equipment Malfunction",
  "Fake Plate",
  "Government Vehicle",
  "Holiday",
  "Incorrect Violation Code",
  "Image Mismatch",
  "Legal Event",
  "Location Error",
  "No End Image",
  "No Plate",
  "No Violation",
  "Partially in Zone",
  "Permitted Vehicle",
  "Plate Glare",
  "Plate Obstructed",
  "Poor Image",
  "Registered Vehicle",
  "Stuck in Traffic",
  "Tow Action",
  "Unreadable License Plate",
  "Vehicle Moving",
  "Wrong License Plate Number",
  "Other",
].map((reason) => ({ label: reason, value: reason.toLowerCase() }));

export interface NoticeReviewActionsProps {
  isIssueEnabled: boolean;
  isVoidEnabled: boolean;
  requiresPolicySelection?: boolean;
  selectedEnforcementPolicyId?: string | null;
  /** Fleet operator selected for one-time assignment on issue (AUT-5032) */
  selectedOperatorId?: string | null;
  intent: EnforcementIntentDetail;
}

export default NoticeReviewActions;
